import React, { FC } from 'react';
import { DialogContent, Grid, TextField, DialogActions, Button, FormControl } from '@mui/material';
import { ApolloCache, Reference, useMutation } from '@apollo/client';
import { createCategoryTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

type AddCategoryFormProps = {
    onAddCategory: (category: { _id: string; name: string }) => void;
    onCloseDialog: () => void;
};
const AddCategoryForm: FC<AddCategoryFormProps> = ({ onAddCategory, onCloseDialog }: AddCategoryFormProps) => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'workflow']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [createCategory] = useMutation(createCategoryTemplate, {
        onCompleted: ({ createCategory }) => {
            onAddCategory(createCategory);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<{ _id: string; name: string }>, { data: { createCategory } }) {
            cache.modify({
                fields: {
                    categories(categoryRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (categoryRefs instanceof Array) {
                            return [...categoryRefs, toReference(createCategory)];
                        }
                        return [categoryRefs, toReference(createCategory)];
                    },
                },
            });
        },
    });

    const validate = (values: { name: string }) => {
        const errors: Record<string, string> = {};
        if (!values.name) {
            errors.name = translate('workflow:VALIDATION:NAME');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            name: '',
        },
        validate,
        enableReinitialize: true,
        onSubmit: async (values) => {
            await createCategory({ variables: { ...values } });
            onCloseDialog();
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                id="category-name"
                                margin="dense"
                                label={translate('actions-materials:CATEGORY_NAME')}
                                required
                                helperText={formik.touched.name && formik.errors.name}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                {...formik.getFieldProps('name')}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseDialog}>{translate('common:CANCEL')}</Button>
                <Button variant="contained" disabled={!formik.isValid} type="submit">
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </form>
    );
};

export default AddCategoryForm;
